import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Flex, Text } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { LuArrowRight, LuCornerDownLeft, LuHistory, LuLock, LuSearch } from 'react-icons/lu'
import { useMenu } from '../../auth/PermissionContext'
import { MenuIcon } from '../../auth/iconMap'
import { resolveMenuLabel } from '../../auth/resolveMenuLabel'
import type { MeMenuItem } from '../../api/services/me.service'
import { accentFor, fonts, mineral, shadows } from '../../theme'
import { canOpen, flattenLeaves, fuzzyScore, readRecents, type NavLeaf } from './nav'

interface Props {
  open: boolean
  onClose: () => void
  onNavigate: (item: MeMenuItem) => void
}

/**
 * Ir a cualquier pantalla escribiendo. El menú de Kuntur pasa de 70 opciones y
 * sigue creciendo: ningún menú visual escala a eso, la búsqueda sí.
 */
export default function CommandPalette({ open, onClose, onNavigate }: Props) {
  const menu = useMenu()
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [cursor, setCursor] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const leaves = useMemo(() => flattenLeaves(menu).filter(l => l.item.route), [menu])
  const label = (i: MeMenuItem) => resolveMenuLabel(i.label, t)

  const results: Array<NavLeaf & { recent?: boolean }> = useMemo(() => {
    const q = query.trim()
    if (!q) {
      const recents = readRecents()
        .map(r => leaves.find(l => l.item.route === r))
        .filter((l): l is NavLeaf => !!l)
        .map(l => ({ ...l, recent: true }))
      const rest = leaves.filter(l => canOpen(l.item) && !recents.some(r => r.item.id === l.item.id)).slice(0, 8 - Math.min(recents.length, 6))
      return [...recents, ...rest]
    }
    return leaves
      .map(l => {
        const hay = `${label(l.item)} ${l.group ? label(l.group) : ''} ${label(l.section)}`
        const s = Math.max(fuzzyScore(q, label(l.item)) * 2, fuzzyScore(q, hay))
        return { l, s: s + (canOpen(l.item) ? 5 : 0) }
      })
      .filter(x => x.s > 5)
      .sort((a, b) => b.s - a.s)
      .slice(0, 12)
      .map(x => x.l)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, leaves, t])

  useEffect(() => {
    if (open) {
      setQuery('')
      setCursor(0)
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${cursor}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [cursor])

  const go = (l: NavLeaf | undefined) => {
    if (!l || !canOpen(l.item)) return
    onClose()
    onNavigate(l.item)
  }

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, results.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); go(results[cursor]) }
    else if (e.key === 'Escape') { e.preventDefault(); onClose() }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="cmdk"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onMouseDown={onClose}
          style={{ position: 'fixed', inset: 0, zIndex: 1400, background: 'rgba(42, 30, 20, 0.42)', backdropFilter: 'blur(6px)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '12vh', paddingInline: 16 }}
        >
          <motion.div
            initial={{ opacity: 0, y: -14, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 520, damping: 36 }}
            onMouseDown={e => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 640 }}
          >
            <Box bg={mineral.surface} borderRadius="18px" boxShadow={shadows.lg} overflow="hidden" border={`1px solid ${mineral.line}`}>
              <Flex align="center" gap={3} px={5} h="62px" borderBottom={`1px solid ${mineral.line}`}>
                <Box color={mineral.ocre}><LuSearch size={20} /></Box>
                <input
                  ref={inputRef}
                  value={query}
                  onChange={e => { setQuery(e.target.value); setCursor(0) }}
                  onKeyDown={onKey}
                  placeholder="¿A dónde vas? Mineros, lotes, recepciones, liquidaciones…"
                  style={{ flex: 1, border: 0, outline: 'none', background: 'transparent', fontSize: 17, fontFamily: fonts.body, color: mineral.ink }}
                />
                <Text as="kbd" fontFamily={fonts.mono} fontSize="11px" px={2} py="2px" borderRadius="6px" bg={mineral.sunk} color={mineral.ink3}>ESC</Text>
              </Flex>

              <Box ref={listRef} maxH="min(60vh, 460px)" overflowY="auto" p={2}>
                {!query && results.some(r => r.recent) && (
                  <Text px={3} pt={2} pb={1} fontFamily={fonts.mono} fontSize="10px" letterSpacing="0.14em" color={mineral.ink3}>RECIENTES</Text>
                )}
                {results.length === 0 && (
                  <Box px={4} py={10} textAlign="center">
                    <Text fontFamily={fonts.display} fontWeight={700} fontSize="17px" color={mineral.ink}>Nada por aquí</Text>
                    <Text fontSize="13px" color={mineral.ink3} mt={1}>Prueba con otra palabra: "minero", "lote", "balanza"…</Text>
                  </Box>
                )}
                {results.map((r, i) => {
                  const acc = accentFor(r.section.code)
                  const on = i === cursor
                  const blocked = !canOpen(r.item)
                  const firstNonRecent = !query && !r.recent && (i === 0 || results[i - 1]?.recent)
                  return (
                    <Box key={r.item.id}>
                      {firstNonRecent && (
                        <Text px={3} pt={3} pb={1} fontFamily={fonts.mono} fontSize="10px" letterSpacing="0.14em" color={mineral.ink3}>SUGERIDOS</Text>
                      )}
                      <Flex
                        data-idx={i}
                        align="center"
                        gap={3}
                        px={3}
                        py={2.5}
                        borderRadius="12px"
                        cursor={blocked ? 'not-allowed' : 'pointer'}
                        position="relative"
                        opacity={blocked ? 0.5 : 1}
                        onMouseMove={() => cursor !== i && setCursor(i)}
                        onClick={() => go(r)}
                      >
                        {on && (
                          <motion.span layoutId="cmdk-cursor" transition={{ type: 'spring', stiffness: 600, damping: 40 }}
                            style={{ position: 'absolute', inset: 0, borderRadius: 12, background: acc.soft, boxShadow: `inset 0 0 0 1px ${acc.color}33` }} />
                        )}
                        <Flex position="relative" w="34px" h="34px" borderRadius="10px" align="center" justify="center" flexShrink={0}
                          bg={on ? acc.color : acc.soft} color={on ? '#fff' : acc.deep} transition="all .15s">
                          {r.recent ? <LuHistory size={16} /> : <MenuIcon name={r.section.icon} size={16} />}
                        </Flex>
                        <Box position="relative" flex={1} minW={0}>
                          <Text fontWeight={600} fontSize="14.5px" color={mineral.ink} lineClamp={1}>{label(r.item)}</Text>
                          <Text fontSize="12px" color={mineral.ink3} lineClamp={1}>
                            {label(r.section)}{r.group ? ` › ${label(r.group)}` : ''}
                          </Text>
                        </Box>
                        <Box position="relative" color={on ? acc.deep : mineral.ink4}>
                          {blocked ? <LuLock size={14} /> : on ? <LuCornerDownLeft size={15} /> : <LuArrowRight size={14} />}
                        </Box>
                      </Flex>
                    </Box>
                  )
                })}
              </Box>

              <Flex gap={4} px={5} py={2.5} borderTop={`1px solid ${mineral.line}`} bg={mineral.surfaceAlt} fontFamily={fonts.mono} fontSize="10.5px" color={mineral.ink3}>
                <Text>↑ ↓ moverse</Text><Text>↵ abrir</Text><Text>esc cerrar</Text>
                <Text ml="auto">{leaves.length} pantallas</Text>
              </Flex>
            </Box>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
